import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Form,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DollarOutlined,
  UserOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import {
  fetchCommissionRecords,
  setFilters,
  setCommissionPagination,
  clearError,
} from '../../features/team/teamSlice';
import { formatDateTime, formatAmount, maskPhone, getStatusColor, getStatusText } from '../../utils/index';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CommissionManagement = () => {
  const dispatch = useDispatch();
  const { commissionRecords, commissionPagination, filters, isLoading, error } = useSelector((state) => state.team);
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    dispatch(fetchCommissionRecords({ ...filters, ...commissionPagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters, commissionPagination.current, commissionPagination.pageSize]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setCommissionPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setCommissionPagination({ current: 1 }));
  };

  // 表格列配置
  const columns = [
    {
      title: '返点编号',
      dataIndex: 'recordNo',
      key: 'recordNo',
      width: 180,
      render: (no) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{no}</span>,
    },
    {
      title: '来源用户',
      key: 'fromUser',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <UserOutlined />
            <span>{record.fromUser.name}</span>
          </Space>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
            {record.fromUser.phone}
          </span>
        </Space>
      ),
    },
    {
      title: '获得用户',
      key: 'toUser',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <UserOutlined />
            <span>{record.toUser.name}</span>
          </Space>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
            {record.toUser.phone}
          </span>
        </Space>
      ),
    },
    {
      title: '订单类型',
      dataIndex: 'commissionType',
      key: 'commissionType',
      render: (type) => {
        const typeMap = {
          mall: { text: '商城订单', color: 'cyan' },
          seckill: { text: '秒杀订单', color: 'orange' },
          recharge: { text: '充值订单', color: 'purple' },
          group_purchase: { text: '拼团订单', color: 'magenta' },
          financial: { text: '理财订单', color: 'gold' },
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '基础金额',
      dataIndex: 'baseAmount',
      key: 'baseAmount',
      render: (amount) => formatAmount(amount),
    },
    {
      title: '返点率',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      render: (rate) => (
        <Tag color="green">{parseFloat(rate).toFixed(2)}%</Tag>
      ),
    },
    {
      title: '返点金额',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      render: (amount) => (
        <Space>
          <DollarOutlined />
          <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
            {formatAmount(amount)}
          </span>
        </Space>
      ),
    },
    {
      title: '层级差',
      dataIndex: 'levelDifference',
      key: 'levelDifference',
      width: 80,
      render: (level) => <Tag>{level}级</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          paid: { text: '已发放', color: 'success' },
          pending: { text: '待发放', color: 'warning' },
          cancelled: { text: '已取消', color: 'default' },
        };
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '发放时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDateTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 查看详情
  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  // 返点配置由系统的commission_configs表统一管理
  // 不需要单独调整每个代理的返点率

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setCommissionPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">返点管理</h1>
        <p className="page-description">管理代理返点和返点率设置</p>
      </div>

      <Card>
        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              placeholder="搜索代理姓名/手机号"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="commissionType">
            <Select placeholder="订单类型" style={{ width: 140 }} allowClear>
              <Option value="mall">商城订单</Option>
              <Option value="seckill">秒杀订单</Option>
              <Option value="recharge">充值订单</Option>
              <Option value="group_purchase">拼团订单</Option>
              <Option value="financial">理财订单</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="返点状态" style={{ width: 120 }} allowClear>
              <Option value="paid">已发放</Option>
              <Option value="pending">待发放</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Space>
            {/* 返点配置由系统的commission_configs表统一管理 */}
          </Space>
          <div>
            共 {commissionPagination.total} 条返点记录
          </div>
        </div>

        {/* 返点记录表格 */}
        <Table
          columns={columns}
          dataSource={commissionRecords}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: commissionPagination.current,
            pageSize: commissionPagination.pageSize,
            total: commissionPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 返点详情抽屉 */}
      <Drawer
        title="返点详情"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="返点金额"
                  value={selectedRecord.commissionAmount}
                  prefix={<DollarOutlined />}
                  formatter={(value) => formatAmount(value)}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="返点率"
                  value={parseFloat(selectedRecord.commissionRate).toFixed(2)}
                  suffix="%"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="层级差"
                  value={selectedRecord.levelDifference}
                  suffix="级"
                />
              </Col>
            </Row>

            <Descriptions
              title="返点信息"
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="返点编号">
                {selectedRecord.recordNo}
              </Descriptions.Item>
              <Descriptions.Item label="来源用户">
                {selectedRecord.fromUser.name} ({selectedRecord.fromUser.phone})
              </Descriptions.Item>
              <Descriptions.Item label="获得用户">
                {selectedRecord.toUser.name} ({selectedRecord.toUser.phone})
              </Descriptions.Item>
              <Descriptions.Item label="订单类型">
                {(() => {
                  const typeMap = {
                    mall: '商城订单',
                    seckill: '秒杀订单',
                    recharge: '充值订单',
                    group_purchase: '拼团订单',
                    financial: '理财订单',
                  };
                  return <Tag color="blue">{typeMap[selectedRecord.commissionType] || selectedRecord.commissionType}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="基础金额">
                {formatAmount(selectedRecord.baseAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="返点率">
                {parseFloat(selectedRecord.commissionRate).toFixed(2)}%
              </Descriptions.Item>
              <Descriptions.Item label="返点金额">
                {formatAmount(selectedRecord.commissionAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="层级差">
                {selectedRecord.levelDifference}级
              </Descriptions.Item>
              <Descriptions.Item label="关联订单">
                {selectedRecord.relatedOrderId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="返点状态">
                {(() => {
                  const statusMap = {
                    paid: { text: '已发放', color: 'success' },
                    pending: { text: '待发放', color: 'warning' },
                    cancelled: { text: '已取消', color: 'default' },
                  };
                  const config = statusMap[selectedRecord.status] || { text: selectedRecord.status, color: 'default' };
                  return <Tag color={config.color}>{config.text}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="发放时间">
                {formatDateTime(selectedRecord.createdAt)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

    </div>
  );
};

export default CommissionManagement;
